<template>
  <div class="chatbot-container">
    <div class="chatbot-header">
      <select v-model="selectedOption">
        <option v-for="option in dropdownOptions" :key="option.value" :value="option.value" >
          {{ option.text }}
        </option>
      </select>
    </div>
    <div class="chatbot-messages" ref="messagesContainer">
      <div v-for="(message, index) in messages" :key="index" :class="['message', message.type]"  @mouseleave="hideCopyButton(index)">
        <div v-if="message.type === 'user'" class="user-message" v-html="message.content">
        </div>
        <div v-else class="bot-message" v-html="message.content" @mouseover="showCopyButton($event, index)"></div>
        <button v-if="copyButtonVisible[index]" @click="copyToClipboard($event, message.content)" class="btn btn-light copy-button" :style="copyBtnStyle">copieren</button>
      </div>
      <div v-if="alertVisible" class="copy-message copy-alert" :style="alertStyle">{{ alertMessage }}</div>
    </div>
    <div class="chatbot-input-area">
      <div class="input-container">
        <button @click="toggleDropdown" class="attachment-button rounded-pill">
          <i class="fa fa-paperclip"></i>
        </button>
        <textarea v-model="userInput" @keyup.enter="sendMessage" placeholder="Schreib was..." rows="1" />
        <button @click="sendMessage" :disabled="loading" class="send-button rounded-pill">
          <i v-if="loading" class="fa fa-cog fa-w-16 fa-spin fa-2x"></i>
          <span v-else><i class="fa fa-paper-plane"></i></span>
        </button>
      </div>
      <input type="file" ref="fileInput" @change="handleFileUpload" style="display: none" :accept="allowedExtensions" />
      <div v-if="showDropdown" class="attachment-dropdown">
        <div class="popup-content">
          <button class="btn btn-icon btn-outline-info" @click="triggerFileUpload('word')">
            <i class="fa fa-file-word-o mr-0-2" aria-hidden="true"></i>Word
          </button>
          <button class="btn btn-icon btn-outline-warning" @click="triggerFileUpload('pdf')">
            <i class="fa fa-file-pdf-o mr-0-2" aria-hidden="true"></i>PDF
          </button>
          <button class="btn btn-icon btn-outline-secondary" @click="triggerFileUpload('pptx')">
            <i class="fa fa-file-powerpoint-o mr-0-2" aria-hidden="true"></i>Power Point
          </button>
          <button class="btn btn-icon btn-outline-success" @click="triggerFileUpload('excel')">
            <i class="fa fa-file-excel-o mr-0-2" aria-hidden="true"></i>Excel
          </button>
        </div>
      </div>
      <div>
        <label class="toggle-switch">
          <input type="checkbox" v-model="deanonymize" />
          <span class="slider"></span>
        </label>
        <div>anonym</div>
      </div>
    </div>
  </div>
</template>

<script>
import markdownit from 'markdown-it';
import {baseURL, service} from '@/utils/request.js';
import {mapState} from "vuex";
import store from "@/store";
import {objectModalName} from "@/utils/enums";

export default {
  data() {
    return {
      userInput: '',
      messages: [],
      loading: false,
      selectedOption: 'de-en',
      dropdownOptions: [
        { value: 'de-en', text: 'Deutsch -> Englisch' },
        { value: 'en-de', text: 'Englisch -> Deutsch' },
        { value: 'fr-de', text: 'Französisch -> Deutsch' },
        { value: 'es-de', text: 'Spanisch -> Deutsch' },
        { value: 'pt-de', text: 'Portugiesisch -> Deutsch' },
        { value: 'pdf-word', text: 'PDF -> Word' },
        { value: 'chat', text: 'Plaudern' }
      ],
      showDropdown: false,
      deanonymize: false,
      copyButtonVisible: {},
      alertVisible: false,
      alertMessage: '',
      alertStyle: {},
      copyBtnStyle: {},
      fileFormat: 'word',
      allowedExtensions: '.doc, .docx',
      downloadLink: null,
      downloadFileName: '',
      characterCount: 0,
      chatHistory:[],
    };
  },
  computed: {
    ...mapState({
      token: state => state.user.token,
    }),
  },
  methods: {
    triggerFileUpload(formatName) {
      if (formatName === 'word') {
        this.allowedExtensions = '.doc, .docx'
        this.fileFormat = 'word'
      } else if (formatName === 'excel') {
        this.allowedExtensions = '.xlsx'
        this.fileFormat = 'excel'
      } else if (formatName === 'pptx') {
        this.allowedExtensions = '.pptx'
        this.fileFormat = 'pptx'
      } else {
        this.allowedExtensions = '.pdf'
        this.fileFormat = 'pdf'
      }
      this.$nextTick(() => {
        this.$refs.fileInput.click();
        this.showDropdown = false
      });
    },
    async handleFileUpload($event) {
      const file = $event.target.files[0];
      const translatedWordFileIndexes = [];
      let url = `${baseURL}/todos/v1/get_deepl_document/`
      if (file) {
        let parts = ['en', 'de']
        if (this.selectedOption) {
          parts = this.selectedOption.split('-')
        } else if (this.selectedOption === 'pdf-word') {
          url = `${baseURL}/todos/v1/convert_to_word/`
        }
        const sourceLanguage = parts[0];
        const targetLanguage = parts[1];

        const formData = new FormData();
        formData.append('file', file);
        formData.append('file_format', this.fileFormat);
        formData.append('source_lang', sourceLanguage);
        formData.append('target_lang', targetLanguage);
        formData.append('deanonymize', !this.deanonymize);
        this.messages.push({type: 'user', content: file.name});
        this.loading = true;
        this.$nextTick(() => {
          this.scrollToBottom();
        });
        let filename = 'downloaded-file'
        if (this.fileFormat === 'word') {
          filename = 'downloaded-file-ai'
          this.messages.push({
            type: 'bot',
            content: '<div class="message-loading"><div class="message-loading-icon"></div>Translating by AI</div>'
          });
          translatedWordFileIndexes.push(this.messages.length - 1);
          fetch(`${baseURL}/todos/v1/get_translated_document_by_ai/`, {
            method: 'POST',
            body: formData,
            headers: {
              'Authorization': 'Token ' + this.token,
            },
          })
            .then(async response => {
              // Extract the filename from the Content-Disposition header
              const disposition = response.headers.get('Content-Disposition');
              if (disposition && disposition.indexOf('attachment') !== -1) {
                const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/; // /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/
                const matches = filenameRegex.exec(disposition);
                if (matches != null && matches[1]) {
                  filename = matches[1].replace(/['"]/g, '');
                } else {
                  // If regex fails, try to extract filename using string manipulation
                  let filenameStart = disposition.indexOf('filename=');
                  if (filenameStart !== -1) {
                    let filename = disposition.substring(filenameStart + 9);
                    // Remove quotes if present
                    if (filename.startsWith('"') || filename.startsWith("'")) {
                      filename = filename.substring(1, filename.length - 1);
                    }
                  }
                }
              }
              // Create a new Blob object using the response data
              const blob = await response.blob();
              const url = window.URL.createObjectURL(blob);
              const downloadDiv = `<a href="${url}" download="${filename}"><i class="fas fa-file-download"></i>${filename}</a>`
              this.messages[translatedWordFileIndexes.shift()] = {type: 'bot', content: downloadDiv};
              if (translatedWordFileIndexes.length === 0) this.$emit('finishedTranslation');
            })
            .catch(error => {
              console.error('Error uploading file:', error);
            }).finally(() => {
            if (translatedWordFileIndexes.length === 0) this.loading = false;
            this.$nextTick(() => {
              this.scrollToBottom();
            })
          });
        }
        filename = 'downloaded-file'
        this.messages.push({type: 'bot', content: '<div class="message-loading"><div class="message-loading-icon"></div>Translating by Deepl</div>'});
        translatedWordFileIndexes.push(this.messages.length - 1);
        fetch(url, {
          method: 'POST',
          body: formData,
          headers: {
            'Authorization': 'Token ' + this.token,
          },
        })
          .then(async response => {
            // Extract the filename from the Content-Disposition header
            const disposition = response.headers.get('Content-Disposition');
            if (disposition && disposition.indexOf('attachment') !== -1) {
              const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/; // /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/
              const matches = filenameRegex.exec(disposition);
              if (matches != null && matches[1]) {
                filename = matches[1].replace(/['"]/g, '');
              } else {
                // If regex fails, try to extract filename using string manipulation
                let filenameStart = disposition.indexOf('filename=');
                if (filenameStart !== -1) {
                  let filename = disposition.substring(filenameStart + 9);
                  // Remove quotes if present
                  if (filename.startsWith('"') || filename.startsWith("'")) {
                    filename = filename.substring(1, filename.length - 1);
                  }
                }
              }
            }
            // Create a new Blob object using the response data
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const downloadDiv = `<a href="${url}" download="${filename}"><i class="fas fa-file-download"></i>${filename}</a>`
            this.messages[translatedWordFileIndexes.shift()] = {type: 'bot', content: downloadDiv};
            if (translatedWordFileIndexes.length === 0) this.$emit('finishedTranslation');
          })
          .catch(error => {
            console.error('Error uploading file:', error);
          }).finally(() => {
          if (translatedWordFileIndexes.length === 0) this.loading = false;
          this.$nextTick(() => {
            this.scrollToBottom();
          })
        });
      }
    },
    async sendMessage() {
      if (this.userInput.trim() === '') return;
      let content = this.userInput;
      this.messages.push({type: 'user', content: this.userInput.replace(/\n/g, '<br>')});
      this.userInput = '';
      this.loading = true;

      this.$nextTick(() => {
        this.scrollToBottom();
      });

      try {
        if (this.selectedOption === 'chat') {
          const response = await this.getChatbotResponse(content);
          const md = new markdownit();
          const answer = response.choices[0].message.content
          this.chatHistory.push({role: 'user', content: content})
          this.chatHistory.push({role: 'assistant', content: answer})
          const htmlResponse = md.render(answer);
          this.messages.push({type: 'bot', content: htmlResponse});
        } else {
          const response = await this.getDeeplResponse(content);
          const reader = response.body.getReader();
          const decoder = new TextDecoder('utf-8');
          let done = false;

          while (!done) {
            const {value, done: streamDone} = await reader.read();
            done = streamDone;
            if (value) {
              const chunk = decoder.decode(value, {stream: true});
              if (this.messages.length > 0 && this.messages[this.messages.length -1].type === 'bot') {
                this.messages[this.messages.length -1].content += '<br><br>' + chunk;
              } else {
                this.messages.push({type: 'bot', content: chunk});
              }
              this.$nextTick(() => {
                this.scrollToBottom();
              });
            }
          }
        }
      } catch (error) {
        console.error('Error getting response:', error);
        this.messages.push({type: 'bot', content: 'Error getting response'});
      } finally {
        this.loading = false;
        this.$nextTick(() => {
          this.scrollToBottom();
        });
      }
    },
    async getDeeplResponse(content) {
      let parts = ['en', 'de']
      if (this.selectedOption) {
        parts = this.selectedOption.split('-')
      }
      const sourceLanguage = parts[0];
      const targetLanguage = parts[1];

      // const response =  await service.request({
      //   url: 'todos/v1/get_deepl_translation/',
      //   method: 'post',
      //   responseType: 'json',
      //   data: {
      //     content: content,
      //     source_language: sourceLanguage,
      //     target_language: targetLanguage,
      //     deanonymize: this.deanonymize,
      //   }
      // })
      const response = await fetch(`${baseURL}/todos/v1/get_deepl_translation/`, {
        method: 'POST',
        headers: {
          'Authorization': 'Token ' + this.token,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
              content: content,
              source_language: sourceLanguage,
              target_language: targetLanguage,
              deanonymize: !this.deanonymize,
            })
      });

      if (response.status === 401) {
        alert('Unauthorized');
        return;
      }

      return response;
    },
    async getChatbotResponse(content) {
      let prompt = [{role: "system", content: "Du bist ein hilfreicher Assistent."}, ...this.chatHistory];
      prompt.push({role: 'user', content: content})

      const data = {
        messages: prompt,
        //max_tokens: 5000,
        temperature: 0.5,
      }

      try {
        return fetch('https://crm.dci-1.com/llm/v1/chat/completions', {
          method: 'POST',
          headers: {
            'Authorization': 'Bearer xrbmcpaitmekecszgwanejnbdeqqvxql',
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(data)
        }).then(response => {
          if (!response.ok) {
            throw new Error('Network response was not ok ' + response.statusText);
          }
          return response.json(); // Parse the JSON from the response
        })
          .catch(error => console.log(error))
      } catch (error) {
        console.error('Error:', error);
      }
    },
    scrollToBottom() {
      const container = this.$refs.messagesContainer;
      container.scrollTop = container.scrollHeight;
    },
    toggleDropdown() {
      this.showDropdown = !this.showDropdown;
    },
    showCopyButton(event, index) {
      this.copyBtnStyle = {
        top: `${event.clientY - 50}px`,
        left: `${event.clientX}px`
      };
      this.$set(this.copyButtonVisible, index, true);
    },
    hideCopyButton(index) {
      this.$set(this.copyButtonVisible, index, false);
    },
    copyToClipboard(event, text) {
      const plain_text = text.replace(/<br>/g, '\n');
      navigator.clipboard.writeText(plain_text).then(() => {
        this.showAlert(event, 'erfolgreich kopiert!')
      }).catch(err => {
        this.showAlert(event, `Fehler beim Kopieren: ${err}`)
      });
    },
    showAlert(event, alertText) {
      this.alertMessage = alertText;
      this.alertStyle = {
        top: `${event.clientY}px`,
        left: `${event.clientX}px`
      };
      this.alertVisible = true;
      setTimeout(() => {
        this.alertVisible = false;
      }, 2000);  // Fade away after 2 seconds
    }
  },
};
</script>

<style scoped>

.chatbot-container {
  display: flex;
  flex-direction: column;
  border: 1px solid #ccc;
  border-radius: 8px;
  /*width: 60%;*/
  height: 800px;
  margin: 0 auto;
  overflow: hidden;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.chatbot-header {
  background-color: #007bff;
  color: white;
  padding: 10px;
  text-align: center;
}

.chatbot-messages {
  flex: 1;
  padding: 10px;
  overflow-y: auto;
  background-color: #f9f9f9;
}

.message {
  margin-bottom: 10px;
}

.user-message {
  text-align: left;
  background-color: #e1f5fe;
  padding: 10px 50px 10px 10px;
  border-radius: 10px;
  max-width: 100%;
  align-self: flex-start;
}

.bot-message {
  text-align: left;
  background-color: #f1f1f1;
  padding: 10px 10px 10px 50px;
  border-radius: 10px;
  max-width: 100%;
  align-self: flex-end;
}

.chatbot-input-area {
  display: flex;
  padding: 10px;
  background-color: #fff;
  border-top: 1px solid #ccc;
}

.toggle-switch {
  position: relative;
  display: inline-block;
  width: 60px;
  height: 34px;
}

.toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  transition: 0.4s;
  border-radius: 34px;
}

.slider:before {
  position: absolute;
  content: "";
  height: 26px;
  width: 26px;
  left: 4px;
  bottom: 4px;
  background-color: white;
  transition: 0.4s;
  border-radius: 50%;
}

input:checked + .slider {
  background-color: #2196F3;
}

input:checked + .slider:before {
  transform: translateX(26px);
}

.input-container {
  display: flex;
  align-items: center;
  border: 2px solid #9c27b0;
  border-radius: 25px;
  padding: 5px;
  width: 100%;
}

textarea {
  flex: 1;
  padding: 10px;
  border: none;
  outline: none;
  border-radius: 25px;
}


.rounded-pill {
  border-radius: 50%!important;
  width: 2.5rem;
  height: 2.5rem;
}

button img {
  width: 20px;
  height: 20px;
}

button:disabled {
  background-color: #ccc;
  cursor: not-allowed;
}

.copy-button {
  position: fixed;
  border: none;
  cursor: pointer;
}

.chatbot-messages:hover + .copy-button {
  display: inline-block;
}


.attachment-button, .send-button {
  border: none;
  cursor: pointer;
  font-size: 20px;
  margin: 0 5px;
}

.attachment-button {
  background-color: #f9f9f9;
  color: #9c27b0;
}

.send-button {
  color: #ffffff;
}

.attachment-dropdown {
  position: absolute;
  transform: translate(-50%, -50%);
  background-color: white;
  border-radius: 10px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  bottom: 50px;
  padding: 5px;
  border: 1px solid #ccc;
  z-index: 1000;
}

.attachment-dropdown button {
  display: block;
  padding: 10px;
  width: 100%;
  border: none;
  text-align: left;
  cursor: pointer;
}

.copy-alert {
  position: fixed;
  background-color: #f8d7da;
  color: #721c24;
  padding: 10px;
  border: 1px solid #f5c6cb;
  border-radius: 5px;
  opacity: 1;
  transition: opacity 0.5s ease-out;
}
.copy-alert.fade-out {
  opacity: 0;
}
.mr-0-2 {
  margin-right: 0.3rem;
}
</style>
